import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Hammer, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

export default function ProducerBanner() {
  const { t } = useLanguage();
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setClosing(true);
    setTimeout(() => setMounted(false), 400);
  };

  if (!mounted) return null;

  return (
    <div
      role="dialog"
      aria-label={t("producer.banner.title")}
      className={`fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-[60] max-w-md sm:max-w-sm md:max-w-md transition-all duration-400 ${
        closing
          ? "opacity-0 translate-y-6 scale-95 pointer-events-none"
          : "opacity-100 translate-y-0 scale-100 animate-in slide-in-from-bottom-8 fade-in duration-500"
      }`}
    >
      <div className="relative rounded-2xl border border-white/15 bg-zinc-900/80 backdrop-blur-xl text-white shadow-2xl overflow-hidden">
        {/* Subtle pattern */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.15) 20px, rgba(255,255,255,0.15) 40px)`,
          }}
        />
        {/* Glow */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl pointer-events-none" />

        {/* Close */}
        <button
          onClick={dismiss}
          aria-label="Close banner"
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-5 sm:p-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-3">
            <Hammer className="w-3 h-3 text-white/70" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/70">
              {t("producer.banner.eyebrow")}
            </span>
          </div>

          <h3 className="text-lg sm:text-xl font-bodoni leading-tight mb-2 pr-6">
            {t("producer.banner.title")}
          </h3>
          <p className="text-sm text-white/75 font-light mb-4 leading-relaxed">
            {t("producer.banner.desc")}
          </p>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              asChild
              size="sm"
              variant="secondary"
              className="group flex-1 hover:scale-[1.02] transition-transform"
              onClick={dismiss}
            >
              <Link to="/semifabricate">
                <span>{t("producer.banner.cta")}</span>
                <ArrowRight className="ml-1.5 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </Button>
            <Button
              asChild
              size="sm"
              variant="outline"
              className="flex-1 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white"
              onClick={dismiss}
            >
              <Link to="/contact">{t("producer.banner.contact")}</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
